import { z } from 'zod';
import { storeCodeSchema } from '../status/store.js';
import { decimalStringSchema, isoDateTimeSchema, objectIdSchema, paginationQuerySchema, sortQuerySchema, queryBooleanSchema, } from './common.js';
/** Composant d'un bundle (`03` §2, `bundles.components`). */
export const bundleComponentSchema = z.object({
    sku: z.string().min(1),
    qty: z.number().int().positive(),
    unitPrice: decimalStringSchema.nullable(),
    order: z.number().int().min(0),
});
export const bundleOverrideSchema = z.object({
    id: objectIdSchema,
    bundleSku: z.string(),
    store: storeCodeSchema,
    disabled: z.boolean(),
    components: z.array(bundleComponentSchema).nullable(),
});
export const bundleListItemSchema = z.object({
    id: objectIdSchema,
    sku: z.string(),
    /** Libellé lisible affiché à la place du SKU ; `null` = pas de libellé, on affiche le SKU. */
    label: z.string().nullable(),
    enabled: z.boolean(),
    componentCount: z.number().int().min(0),
    /** Dernière commande préparée qui a consommé ce bundle ; `null` = jamais servi. */
    lastUsedAt: isoDateTimeSchema.nullable(),
    updatedAt: isoDateTimeSchema,
    updatedBy: objectIdSchema.nullable(),
    updatedByName: z.string().nullable(),
});
export const bundleDetailSchema = z.object({
    ...bundleListItemSchema.shape,
    components: z.array(bundleComponentSchema),
    overrides: z.array(bundleOverrideSchema),
});
export const bundleListQuerySchema = z.object({
    ...paginationQuerySchema.shape,
    ...sortQuerySchema.shape,
    /** Recherche par SKU ou libellé de bundle, **ou** SKU de composant (`02` §8). */
    search: z.string().min(1).optional(),
    enabled: queryBooleanSchema.optional(),
});
export const bundleInputSchema = z.object({
    sku: z.string().min(1),
    /**
     * Libellé optionnel affiché à la place du SKU (ex. « Crème x4 » pour
     * `3701730701034x4`). Vide = pas de libellé. Absent sur un `PUT` = libellé
     * inchangé.
     */
    label: z.string().trim().max(120).optional(),
    enabled: z.boolean().default(true),
    components: z.array(bundleComponentSchema).min(1, 'Au moins un composant est requis'),
});
// Les imports portent le SKU comme libellé technique quand aucun libellé
// n'existe. Sa longueur ne doit pas être limitée comme celle d'un libellé saisi.
const bundleImportItemSchema = bundleInputSchema.extend({
    label: z.string().trim().optional(),
}).superRefine((item, ctx) => {
    if (item.label !== undefined && item.label !== item.sku && item.label.length > 120) {
        ctx.addIssue({
            code: 'custom',
            path: ['label'],
            message: 'Le libellé ne peut pas dépasser 120 caractères.',
        });
    }
});
export const bundleOverrideInputSchema = z.object({
    store: storeCodeSchema,
    disabled: z.boolean().default(false),
    components: z.array(bundleComponentSchema).nullable(),
});
/** Résultat du champ « Tester un SKU » (`02` §8). */
export const bundleExpansionSchema = z.object({
    sku: z.string(),
    store: storeCodeSchema.nullable(),
    matched: z.boolean(),
    source: z.enum(['global', 'override', 'none']),
    components: z.array(bundleComponentSchema),
});
/** Aperçu d'un import CSV de bundles (`02` §8). */
export const bundleImportPreviewSchema = z.object({
    format: z.enum(['matrix', 'simple']),
    /** `null` pour le format simple ; `components` ou `lookup` pour une matrice. */
    matrixMode: z.enum(['components', 'lookup']).nullable(),
    created: z.array(bundleImportItemSchema),
    updated: z.array(bundleImportItemSchema),
    unchanged: z.number().int().min(0),
    errors: z.array(z.string()),
    warnings: z.array(z.string()),
});
/** Corps de l'aperçu d'import (`02` §8) : contenu CSV brut, disposition devinée sauf indication contraire. */
export const bundleImportPreviewBodySchema = z.object({
    content: z.string().min(1),
    format: z.enum(['matrix', 'simple']).optional(),
    matrixMode: z.enum(['components', 'lookup']).optional(),
});
/**
 * Confirmation d'écriture (`02` §8) : le client renvoie exactement les bundles
 * affichés par l'aperçu (créés et modifiés), sans reparser le CSV côté serveur.
 */
export const bundleImportConfirmBodySchema = z.object({
    items: z.array(bundleImportItemSchema).min(1),
});
/** Corps du champ « Tester un SKU » (`02` §8). */
export const bundleTestBodySchema = z.object({
    sku: z.string().min(1),
    qty: z.number().int().positive().default(1),
});
/** Ligne résultante d'une expansion (`domain/bundles` `ExpandedLine`). */
export const bundleExpansionLineSchema = z.object({
    reference: z.string(),
    sku: z.string(),
    quantity: z.number().int(),
    unitPrice: decimalStringSchema,
    discount: decimalStringSchema,
    origin: z.object({
        type: z.enum(['line', 'bundle']),
        sourceLineId: z.string(),
        bundleSku: z.string().nullable(),
    }),
});
export const bundleExpansionWarningSchema = z.object({
    code: z.string(),
    message: z.string(),
    lineId: z.string().nullable(),
    bundleSku: z.string().nullable(),
    sku: z.string().nullable(),
});
/** Résultat complet du champ « Tester un SKU » (`02` §8, `domain/bundles` `BundleTestResult`). */
export const bundleTestResultSchema = z.object({
    sku: z.string(),
    store: storeCodeSchema.nullable(),
    matched: z.boolean(),
    source: z.enum(['global', 'override', 'none']),
    label: z.string().nullable(),
    lines: z.array(bundleExpansionLineSchema),
    warnings: z.array(bundleExpansionWarningSchema),
});
/**
 * Résultat de la création/modification d'un bundle (`02` §8) : le détail plus
 * les avertissements non bloquants (composant lui-même un bundle, `01` §2.2.c).
 */
export const bundleSaveResultSchema = z.object({
    bundle: bundleDetailSchema,
    warnings: z.array(z.string()),
});
/** Résultat de `POST /api/bundles/import/confirm` (`02` §8). */
export const bundleImportConfirmResultSchema = z.object({
    created: z.number().int().min(0),
    updated: z.number().int().min(0),
});
//# sourceMappingURL=bundles.js.map